import React, { useMemo, useState, useCallback } from "react";
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import ConfirmDeleteDialog from "../../shared/ConfirmDeleteDialog";
import EditableTableCell from "./EditableTableCell";
import {
  PAGE_TYPE_MASTER,
  PRIORITY_MASTER,
} from "../../../constants/suggestOptions";

const cellBorderSx = {
  border: "1px solid",
  borderColor: "divider",
};

// セル内の高さを揃えるための共通ラッパー
const cellInnerSx = {
  minHeight: 40,
  display: "flex",
  alignItems: "center",
  width: "100%",
  px: 1,
  py: 0.5,
  boxSizing: "border-box",
};

// value → label 変換
const labelOf = (options, v) =>
  options.find((o) => o.value === v)?.label ?? v ?? "-";

export default function SuggestListTable({ items = [], setItems, onRefetch }) {
  // EditableTableCell用に {value,label} の形へ揃える
  const pageTypeOptions = useMemo(() => PAGE_TYPE_MASTER, []);
  const priorityOptions = useMemo(() => PRIORITY_MASTER, []);

  // setItems が渡っていない場合に備えて安全に呼べるようにする
  const safeSetItems = typeof setItems === "function" ? setItems : null;

  // 削除ダイアログ
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // 編集中セル情報
  // editing = { id, field: "content" | "priority" | "page_type", value }
  const [editing, setEditing] = useState(null);

  // 保存中の行id（保存中は編集/削除をロック）
  const [savingIds, setSavingIds] = useState(() => new Set());
  const isSaving = (id) => savingIds.has(id);

  const [deletingIds, setDeletingIds] = useState(() => new Set());
  const isRowDeleting = (id) => deletingIds.has(id);

  // CSRF token（metaから取得）
  const getCsrf = () =>
    document.querySelector('meta[name="csrf-token"]')?.content;

  // PATCH（更新）API
  const patchSuggestion = useCallback(async (id, attrs) => {
    const res = await fetch(`/company/suggestions/${id}.json`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": getCsrf(),
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ suggestion: attrs }),
      credentials: "same-origin",
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      console.error("Failed to update suggestion", {
        id,
        status: res.status,
        body: text,
      });
      throw new Error(
        "更新に失敗しました。時間をおいてから再度お試しください。",
      );
    }
  }, []);

  // optimistic update（即反映）用ヘルパー
  const applyOptimisticPatch = (id, patch) => {
    safeSetItems?.((prev) =>
      prev.map((i) => (i.id === id ? { ...i, ...patch } : i)),
    );
  };

  const rollbackRow = (id, beforeRow) => {
    safeSetItems?.((prev) => prev.map((i) => (i.id === id ? beforeRow : i)));
  };

  const removeOptimistically = (id) => {
    safeSetItems?.((prev) => prev.filter((i) => i.id !== id));
  };

  // セル編集開始
  const startEdit = (item, field) => {
    if (isSaving(item.id)) return;
    if (editing?.id === item.id && editing?.field === field) return;

    setEditing({
      id: item.id,
      field,
      value: item[field] ?? "",
    });
  };

  const cancelEdit = () => setEditing(null);

  /**
   * セル保存（optimistic）
   * 1) まず画面に即反映
   * 2) サーバへPATCH
   * 3) 成功：toast + refetch
   * 4) 失敗：ロールバック + エラーtoast
   */
  const saveCell = async ({ id, field, value }) => {
    const current = items.find((i) => i.id === id);
    if (!current) return;

    // 文字列はtrimして比較
    const nextValue = field === "content" ? (value ?? "").trim() : value;

    const currentValue =
      field === "content"
        ? (current.content ?? "").trim()
        : field === "priority"
          ? current.priority
          : current.page_type;

    // 変更がなければ何もしない
    if (nextValue === currentValue) return;

    // contentフィールドで空の場合はエラー
    if (field === "content" && !nextValue) {
      toast.error("内容は必須です。");
      return;
    }

    // 送るattrs
    const attrs =
      field === "content"
        ? { content: nextValue }
        : field === "priority"
          ? { priority: nextValue }
          : { page_type: nextValue };

    setSavingIds((prev) => new Set(prev).add(id));

    // ロールバック用に元行を保持
    const beforeRow = current;

    // 即反映
    applyOptimisticPatch(id, attrs);

    try {
      // サーバ更新
      await patchSuggestion(id, attrs);
      toast.success("保存しました。");

      // 最終整合性（refetch失敗は保存失敗扱いにしない）
      onRefetch?.().catch((e) => console.warn("refetch failed", e));
    } catch (e) {
      // 失敗したら元に戻す
      rollbackRow(id, beforeRow);
      console.error("保存に失敗しました。", e);
      toast.error("保存に失敗しました。");
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  // フォーカスアウト時に保存
  const handleBlurSave = async () => {
    if (!editing) return;
    const payload = { ...editing };
    setEditing(null);
    await saveCell(payload);
  };

  // Enterで保存 / Escでキャンセル
  const handleKeyDown = async (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      await handleBlurSave();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  };

  // 削除ダイアログを開く
  const handleOpenDialog = (id) => {
    if (!id) return;
    setDeleteTargetId(id);
    setShowDialog(true);
  };

  // 削除ダイアログを閉じる（削除中は不可）
  const handleCloseDialog = () => {
    if (isDeleting) return;
    setShowDialog(false);
    setDeleteTargetId(null);
  };

  // 削除確定処理 (DELETE)
  const handleConfirmDelete = async () => {
    if (!deleteTargetId || isDeleting) return;

    setIsDeleting(true);
    const targetId = deleteTargetId;

    setDeletingIds((prev) => new Set(prev).add(targetId));
    setEditing((prev) => (prev?.id === targetId ? null : prev));

    try {
      const res = await fetch(`/company/suggestions/${targetId}.json`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrf(),
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });

      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);

      toast.success("削除しました");
      handleCloseDialog();

      removeOptimistically(targetId);

      //最終整合性
      onRefetch?.().catch((e) => console.warn("refetch failed", e));
    } catch (e) {
      console.error(e);
      toast.error("削除に失敗しました");
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(targetId);
        return next;
      });
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 2 }}>
        <Table
          size="small"
          sx={{
            borderCollapse: "collapse",
            "& th": {
              ...cellBorderSx,
              fontWeight: "bold",
              backgroundColor: "grey.100",
            },
            "& td": { ...cellBorderSx, p: 0, verticalAlign: "top" },
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  textAlign: "center",
                  verticalAlign: "middle",
                },
              }}
            >
              <TableCell sx={{ width: 120, p: 1 }}>作成日</TableCell>
              <TableCell sx={{ width: 140, p: 1 }}>対象画面</TableCell>
              <TableCell sx={{ p: 1 }}>内容</TableCell>
              <TableCell sx={{ width: 110, p: 1 }}>優先度</TableCell>
              <TableCell sx={{ width: 70, p: 1 }}>削除</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {items.map((item) => {
              const editingContent =
                editing?.id === item.id && editing?.field === "content";
              const editingPriority =
                editing?.id === item.id && editing?.field === "priority";
              const editingPageType =
                editing?.id === item.id && editing?.field === "page_type";
              const rowDeleting = isRowDeleting(item.id);

              return (
                <TableRow
                  key={item.id}
                  hover
                  sx={{
                    opacity: rowDeleting ? 0.5 : 1,
                    pointerEvents: rowDeleting ? "none" : "auto",
                  }}
                >
                  {/* 作成日（表示のみ） */}
                  <TableCell>
                    <Box sx={{ ...cellInnerSx, justifyContent: "center" }}>
                      {item.created_at}
                    </Box>
                  </TableCell>

                  {/* 対象画面（Select編集） */}
                  <EditableTableCell
                    align="center"
                    type="select"
                    value={editingPageType ? editing.value : item.page_type}
                    displayValue={labelOf(pageTypeOptions, item.page_type)}
                    editing={editingPageType}
                    options={pageTypeOptions}
                    onStartEdit={() => startEdit(item, "page_type")}
                    onChange={(v) => setEditing((p) => ({ ...p, value: v }))}
                    onBlurSave={handleBlurSave}
                    onKeyDown={handleKeyDown}
                    disabled={isSaving(item.id) || rowDeleting}
                  />

                  {/* 内容（Text編集） */}
                  <EditableTableCell
                    align="left"
                    type="text"
                    value={editingContent ? editing.value : item.content}
                    editing={editingContent}
                    onStartEdit={() => startEdit(item, "content")}
                    onChange={(v) => setEditing((p) => ({ ...p, value: v }))}
                    onBlurSave={handleBlurSave}
                    onKeyDown={handleKeyDown}
                    disabled={isSaving(item.id) || rowDeleting}
                  />

                  {/* 優先度（Select編集） */}
                  <EditableTableCell
                    align="center"
                    type="select"
                    value={editingPriority ? editing.value : item.priority}
                    displayValue={labelOf(priorityOptions, item.priority)}
                    editing={editingPriority}
                    options={priorityOptions}
                    onStartEdit={() => startEdit(item, "priority")}
                    onChange={(v) => setEditing((p) => ({ ...p, value: v }))}
                    onBlurSave={handleBlurSave}
                    onKeyDown={handleKeyDown}
                    disabled={isSaving(item.id) || rowDeleting}
                  />

                  {/* 削除（ダイアログ表示） */}
                  <TableCell align="center">
                    <Box
                      sx={{
                        ...cellInnerSx,
                        justifyContent: "center",
                        py: 0,
                      }}
                    >
                      {rowDeleting ? (
                        <Tooltip title="削除中...">
                          <CircularProgress size={18} />
                        </Tooltip>
                      ) : (
                        <Tooltip title="削除">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDialog(item.id);
                            }}
                            aria-label="delete"
                            disabled={isSaving(item.id)}
                          >
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <ConfirmDeleteDialog
        open={showDialog}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
